/**
 * localStorage utility for managing user preferences and app state
 */

/**
 * Get a value from localStorage with error handling
 * @param {string} key - The localStorage key
 * @param {*} defaultValue - Default value if key doesn't exist or parsing fails
 * @returns {*} The parsed value or default value
 */
export const getFromStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item);
  } catch (error) {
    console.warn(`Failed to parse localStorage item "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Set a value in localStorage with error handling
 * @param {string} key - The localStorage key
 * @param {*} value - The value to store (will be JSON stringified)
 * @returns {boolean} True if successful, false otherwise
 */
export const setInStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`Failed to set localStorage item "${key}":`, error);
    return false;
  }
};

/**
 * Remove a value from localStorage with error handling
 * @param {string} key - The localStorage key
 * @returns {boolean} True if successful, false otherwise
 */
export const removeFromStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove localStorage item "${key}":`, error);
    return false;
  }
};

/**
 * Storage keys for different app features
 */
export const STORAGE_KEYS = {
  STACK_FILTERS: 'sortafriends_stack_filters',
  USER_PREFERENCES: 'sortafriends_user_preferences',
  // Add more keys as needed for other features
};

/**
 * Default stack filter preferences
 */
export const DEFAULT_STACK_FILTERS = {
  updatesOnly: false,
  friendsOnly: false,
  noBots: false,
};

/**
 * Get stack filter preferences from localStorage
 * @returns {Object} Stack filter preferences
 */
export const getStackFilters = () => {
  return getFromStorage(STORAGE_KEYS.STACK_FILTERS, DEFAULT_STACK_FILTERS);
};

/**
 * Save stack filter preferences to localStorage
 * @param {Object} filters - Stack filter preferences
 * @returns {boolean} True if successful, false otherwise
 */
export const saveStackFilters = (filters) => {
  return setInStorage(STORAGE_KEYS.STACK_FILTERS, filters);
};

/**
 * Update a specific stack filter preference
 * @param {string} filterName - The filter name (e.g., 'updatesOnly', 'friendsOnly', 'noBots')
 * @param {boolean} value - The new value
 * @returns {boolean} True if successful, false otherwise
 */
export const updateStackFilter = (filterName, value) => {
  const currentFilters = getStackFilters();
  const updatedFilters = {
    ...currentFilters,
    [filterName]: value,
  };
  return saveStackFilters(updatedFilters);
};

/**
 * Reset stack filters to default values
 * @returns {boolean} True if successful, false otherwise
 */
export const resetStackFilters = () => {
  return saveStackFilters(DEFAULT_STACK_FILTERS);
};
